// packages/factory-admin/src/pages/DealerRewardsPage.tsx
// Light theme for factory admin

import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface Dealer {
  dealer_id: string;
  company_name: string;
  email: string;
  current_points: number;
  lifetime_points: number;
  ytd_points_earned: number;
  ytd_orders_count: number;
  last_order_date: string | null;
  tier_name: string;
  tier_display: string;
  tier_icon: string;
  tier_color: string;
  discount_percent: number;
}

interface Tier {
  id: string;
  name: string;
  display_name: string;
  min_points: number;
  max_points: number | null;
  discount_percent: number;
  color: string;
  icon: string;
  perks: string[];
  description: string;
}

interface MarginCategory {
  id: string;
  product_type: string;
  display_name: string;
  margin_multiplier: number;
  description: string;
}

export default function DealerRewardsPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [marginCategories, setMarginCategories] = useState<MarginCategory[]>([]);
  const [selectedDealer, setSelectedDealer] = useState<any>(null);
  const [filters, setFilters] = useState({ tier: '', search: '', sortBy: 'points' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'dealers' | 'settings' | 'analytics'>('dealers');
  const [analytics, setAnalytics] = useState<any>(null);

  // Adjustment modal state
  const [adjustModal, setAdjustModal] = useState<{ dealerId: string; dealerName: string } | null>(null);
  const [adjustPoints, setAdjustPoints] = useState<number>(0);
  const [adjustReason, setAdjustReason] = useState('');

  // Activity award modal
  const [activityModal, setActivityModal] = useState<{ dealerId: string; dealerName: string } | null>(null);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [activities, setActivities] = useState<any[]>([]);
  const [engagementActivities, setEngagementActivities] = useState<any[]>([]);
  const [editingActivity, setEditingActivity] = useState<string | null>(null);
  const [activitySaving, setActivitySaving] = useState(false);

  useEffect(() => {
    loadInitialData();
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (filters.tier || filters.search || filters.sortBy) {
      loadDealers();
    }
  }, [filters]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [dealersRes, tiersRes, marginRes, engagementRes] = await Promise.all([
        api.get('/rewards/admin/dealers?sortBy=points&limit=100'),
        api.get('/rewards/tiers'),
        api.get('/rewards/admin/margin-categories'),
        api.get('/rewards/admin/engagement-activities').catch(() => []),
      ]);
      setDealers(dealersRes.dealers);
      setTiers(tiersRes);
      setMarginCategories(marginRes);
      setEngagementActivities(engagementRes);
    } catch (err) {
      console.error('Failed to load rewards data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDealers = async () => {
    try {
      const params = new URLSearchParams({
        sortBy: filters.sortBy,
        limit: '100',
      });
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.search) params.append('search', filters.search);
      
      const res = await api.get(`/rewards/admin/dealers?${params}`);
      setDealers(res.dealers);
    } catch (err) {
      console.error('Failed to load dealers:', err);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await api.get('/rewards/admin/analytics?period=30d');
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  };

  const loadActivities = async () => {
    try {
      const data = await api.get('/rewards/activities');
      setActivities(data);
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  };

  const handleAdjustPoints = async () => {
    if (!adjustModal || adjustPoints === 0 || !adjustReason.trim()) {
      alert('Please enter points and a reason');
      return;
    }
    
    try {
      await api.post(`/rewards/admin/dealers/${adjustModal.dealerId}/adjust`, {
        points: adjustPoints,
        reason: adjustReason,
      });
      alert(`Successfully ${adjustPoints > 0 ? 'added' : 'removed'} ${Math.abs(adjustPoints)} points`);
      setAdjustModal(null);
      setAdjustPoints(0);
      setAdjustReason('');
      loadDealers();
    } catch (err: any) {
      alert(err.message || 'Failed to adjust points');
    }
  };

  const handleAwardActivity = async () => {
    if (!activityModal || !selectedActivity) {
      alert('Please select an activity');
      return;
    }
    
    try {
      const result = await api.post(`/rewards/admin/dealers/${activityModal.dealerId}/activity`, {
        activityType: selectedActivity,
        notes: 'Manually awarded by admin',
      });
      alert(`Successfully awarded ${result.pointsAwarded} points!`);
      setActivityModal(null);
      setSelectedActivity('');
      loadDealers();
    } catch (err: any) {
      alert(err.message || 'Failed to award activity');
    }
  };

  const viewDealerDetail = async (dealerId: string) => {
    try {
      const detail = await api.get(`/rewards/admin/dealers/${dealerId}`);
      setSelectedDealer(detail);
    } catch (err) {
      console.error('Failed to load dealer detail:', err);
    }
  };

  const refreshLeaderboard = async () => {
    try {
      await api.post('/rewards/admin/refresh-leaderboard');
      alert('Leaderboard refreshed!');
    } catch (err) {
      alert('Failed to refresh leaderboard');
    }
  };

  const getTierCount = (tierName: string) => {
    if (analytics?.tierDistribution) {
      const tier = analytics.tierDistribution.find((t: any) => t.name === tierName);
      if (tier) return parseInt(tier.dealer_count);
    }
    return dealers.filter(d => d.tier_name === tierName).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dealer Rewards Program</h1>
          <p className="text-gray-500">Manage dealer points, tiers, and training</p>
        </div>
        <button
          onClick={refreshLeaderboard}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
        >
          🔄 Refresh Leaderboard
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { id: 'dealers', label: '👥 Dealers' },
          { id: 'settings', label: '⚙️ Settings' },
          { id: 'analytics', label: '📊 Analytics' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id as any);
              if (tab.id === 'analytics') loadAnalytics();
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* DEALERS TAB */}
      {activeTab === 'dealers' && (
        <>
          {/* Tier Summary Cards */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {tiers.map((tier) => {
              const count = getTierCount(tier.name);
              const isSelected = filters.tier === tier.name;
              return (
                <button
                  key={tier.name}
                  onClick={() => setFilters({ ...filters, tier: isSelected ? '' : tier.name })}
                  className={`p-3 rounded-lg text-center transition-all border-2 ${
                    isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: tier.color + '15', 
                    borderColor: isSelected ? tier.color : tier.color + '40'
                  }}
                >
                  <div className="text-2xl">{tier.icon}</div>
                  <div className="text-xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-500 capitalize">{tier.name}</div>
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <input
              type="text"
              placeholder="Search dealers..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={filters.tier}
              onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Tiers</option>
              {tiers.map((tier) => (
                <option key={tier.name} value={tier.name}>{tier.display_name}</option>
              ))}
            </select>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="points">Sort by Points</option>
              <option value="name">Sort by Name</option>
              <option value="lastOrder">Sort by Last Order</option>
              <option value="tier">Sort by Tier</option>
            </select>
          </div>

          {/* Dealers Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Dealer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Points</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">YTD Points</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Orders YTD</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Order</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dealers.map((dealer) => (
                  <tr key={dealer.dealer_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{dealer.company_name}</div>
                      <div className="text-sm text-gray-500">{dealer.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: dealer.tier_color + '20', color: dealer.tier_color }}
                      >
                        {dealer.tier_icon} {dealer.tier_display}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-gray-900">{(dealer.current_points || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {(dealer.ytd_points_earned || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {dealer.ytd_orders_count || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {dealer.last_order_date 
                        ? new Date(dealer.last_order_date).toLocaleDateString()
                        : <span className="text-gray-400">Never</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => viewDealerDetail(dealer.dealer_id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </button>
                        <button
                          onClick={() => setAdjustModal({ 
                            dealerId: dealer.dealer_id, 
                            dealerName: dealer.company_name 
                          })}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Adjust
                        </button>
                        <button
                          onClick={() => {
                            setActivityModal({ dealerId: dealer.dealer_id, dealerName: dealer.company_name });
                            loadActivities();
                          }}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          Award
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {dealers.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No dealers found
              </div>
            )}
          </div>
        </>
      )}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tier Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tier Configuration</h3>
            <p className="text-gray-500 text-sm mb-4">Point thresholds and discount rates for each tier</p>
            <div className="space-y-3">
              {tiers.map((tier) => (
                <div 
                  key={tier.id} 
                  className="flex items-center gap-4 p-4 rounded-lg border"
                  style={{ backgroundColor: tier.color + '08', borderColor: tier.color + '30' }}
                >
                  <span className="text-3xl">{tier.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{tier.display_name}</div>
                    <div className="text-sm text-gray-500">
                      {tier.min_points.toLocaleString()} - {tier.max_points?.toLocaleString() || '∞'} points
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-gray-900">{tier.discount_percent}%</div>
                    <div className="text-xs text-gray-500">discount</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Margin Multipliers */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Point Multipliers by Product</h3>
            <p className="text-gray-500 text-sm mb-4">Higher-margin products earn more points per dollar spent</p>
            <div className="space-y-3">
              {marginCategories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{cat.display_name}</div>
                    <div className="text-sm text-gray-500">{cat.description}</div>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {cat.margin_multiplier}x
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Formula:</strong> Points = (Order Total × Multiplier) ÷ 100
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Example: $5,000 CVW order = (5000 × 1.5) ÷ 100 = 75 points
              </p>
            </div>
          </div>

          {/* Engagement Activities Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Engagement Activities</h3>
                <p className="text-gray-500 text-sm mt-1">Configure point values for dealer engagement activities. Click any row to edit.</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    await api.post('/rewards/admin/engagement-activities', {
                      display_name: 'New Activity',
                      description: '',
                      points_awarded: 100,
                      frequency: 'per_occurrence',
                    });
                    const updated = await api.get('/rewards/admin/engagement-activities');
                    setEngagementActivities(updated);
                    // Auto-edit the new one
                    const newest = updated[updated.length - 1];
                    if (newest) setEditingActivity(newest.id);
                  } catch (err: any) {
                    alert(err.message || 'Failed to create activity');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Activity
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Activity</th>
                    <th className="text-left p-3 font-medium text-gray-600">Description</th>
                    <th className="text-center p-3 font-medium text-gray-600">Points</th>
                    <th className="text-center p-3 font-medium text-gray-600">Frequency</th>
                    <th className="text-center p-3 font-medium text-gray-600">Active</th>
                    <th className="text-center p-3 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {engagementActivities.map((act) => (
                    <tr key={act.id} className="border-t border-gray-100 hover:bg-gray-50">
                      {editingActivity === act.id ? (
                        <>
                          <td className="p-3">
                            <input
                              type="text"
                              defaultValue={act.display_name}
                              onBlur={(e) => { act._editName = e.target.value; }}
                              onChange={(e) => { act._editName = e.target.value; }}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="p-3">
                            <input
                              type="text"
                              defaultValue={act.description || ''}
                              onBlur={(e) => { act._editDesc = e.target.value; }}
                              onChange={(e) => { act._editDesc = e.target.value; }}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="p-3 text-center">
                            <input
                              type="number"
                              defaultValue={act.points_awarded}
                              onBlur={(e) => { act._editPoints = Number(e.target.value); }}
                              onChange={(e) => { act._editPoints = Number(e.target.value); }}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                              min={0}
                            />
                          </td>
                          <td className="p-3 text-center">
                            <select
                              defaultValue={act.frequency}
                              onChange={(e) => { act._editFreq = e.target.value; }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="one_time">One-time</option>
                              <option value="per_occurrence">Per occurrence</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="annual">Annual</option>
                            </select>
                          </td>
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              defaultChecked={act.active}
                              onChange={(e) => { act._editActive = e.target.checked; }}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex gap-1 justify-center">
                              <button
                                onClick={async () => {
                                  setActivitySaving(true);
                                  try {
                                    await api.patch(`/rewards/admin/engagement-activities/${act.id}`, {
                                      display_name: act._editName ?? act.display_name,
                                      description: act._editDesc ?? act.description,
                                      points_awarded: act._editPoints ?? act.points_awarded,
                                      frequency: act._editFreq ?? act.frequency,
                                      active: act._editActive ?? act.active,
                                    });
                                    const updated = await api.get('/rewards/admin/engagement-activities');
                                    setEngagementActivities(updated);
                                    setEditingActivity(null);
                                  } catch (err: any) {
                                    alert(err.message || 'Failed to save');
                                  } finally {
                                    setActivitySaving(false);
                                  }
                                }}
                                disabled={activitySaving}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                              >
                                {activitySaving ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEditingActivity(null)}
                                className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3">
                            <span className="mr-2">{act.icon || '⭐'}</span>
                            <span className="font-medium text-gray-900">{act.display_name}</span>
                          </td>
                          <td className="p-3 text-gray-500">{act.description || '—'}</td>
                          <td className="p-3 text-center">
                            <span className="font-bold text-green-600">{act.points_awarded}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              {act.frequency.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {act.active ? (
                              <span className="text-green-600">Active</span>
                            ) : (
                              <span className="text-red-400">Disabled</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => setEditingActivity(act.id)}
                              className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs font-medium"
                            >
                              Edit
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {engagementActivities.length === 0 && (
              <p className="text-center text-gray-400 py-8">No engagement activities configured</p>
            )}
          </div>
        </div>
      )}

      {/* ANALYTICS TAB */}
      {activeTab === 'analytics' && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Points Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Points Activity (Last 30 Days)</h3>
            <div className="space-y-3">
              {analytics.transactionSummary.map((item: any) => (
                <div key={item.transaction_type} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                  <span className="text-gray-700 capitalize">{item.transaction_type.replace(/_/g, ' ')}</span>
                  <div className="text-right">
                    <span className="font-semibold text-green-600">
                      +{parseInt(item.points_awarded).toLocaleString()}
                    </span>
                    <span className="text-gray-400 text-sm ml-2">
                      ({item.transaction_count} transactions)
                    </span>
                  </div>
                </div>
              ))}
              {analytics.transactionSummary.length === 0 && (
                <p className="text-gray-500 text-center py-4">No activity in the last 30 days</p>
              )}
            </div>
          </div>
          
          {/* Tier Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dealer Distribution by Tier</h3>
            <div className="space-y-3">
              {analytics.tierDistribution.map((tier: any) => {
                const total = analytics.tierDistribution.reduce((sum: number, t: any) => sum + parseInt(t.dealer_count), 0);
                const percent = total > 0 ? (parseInt(tier.dealer_count) / total * 100).toFixed(1) : 0;
                return (
                  <div key={tier.display_name} className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tier.color }}
                    />
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-700">{tier.display_name}</span>
                        <span className="font-semibold">{tier.dealer_count} ({percent}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${percent}%`,
                            backgroundColor: tier.color
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ADJUSTMENT MODAL */}
      {adjustModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Adjust Points</h3>
            <p className="text-gray-500 mb-4">{adjustModal.dealerName}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Points (+ to add, - to remove)</label>
                <input
                  type="number"
                  value={adjustPoints}
                  onChange={(e) => setAdjustPoints(parseInt(e.target.value) || 0)}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 500 or -100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Explain the adjustment..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setAdjustModal(null);
                  setAdjustPoints(0);
                  setAdjustReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustPoints}
                disabled={adjustPoints === 0 || !adjustReason.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Adjustment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVITY AWARD MODAL */}
      {activityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Award Activity Points</h3>
            <p className="text-gray-500 mb-4">{activityModal.dealerName}</p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Activity</label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {activities.map((activity) => (
                  <label
                    key={activity.activity_type}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedActivity === activity.activity_type 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="activity"
                        value={activity.activity_type}
                        checked={selectedActivity === activity.activity_type}
                        onChange={(e) => setSelectedActivity(e.target.value)}
                        className="text-blue-600"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          {activity.icon} {activity.display_name}
                        </div>
                        <div className="text-xs text-gray-500">{activity.description}</div>
                      </div>
                    </div>
                    <span className="text-green-600 font-semibold">+{activity.points_awarded}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setActivityModal(null);
                  setSelectedActivity('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAwardActivity}
                disabled={!selectedActivity}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Award Points
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DEALER DETAIL MODAL */}
      {selectedDealer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-auto shadow-2xl">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Dealer Details</h3>
                <p className="text-gray-500">{selectedDealer.tier?.displayName}</p>
              </div>
              <button 
                onClick={() => setSelectedDealer(null)} 
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-gray-900">{selectedDealer.currentPoints?.toLocaleString()}</div>
                <div className="text-sm text-gray-500">Current Points</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-gray-900">{selectedDealer.tier?.discountPercent}%</div>
                <div className="text-sm text-gray-500">Discount Rate</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {selectedDealer.training?.completed}/{selectedDealer.training?.total}
                </div>
                <div className="text-sm text-gray-500">Training Complete</div>
              </div>
            </div>

            <h4 className="font-semibold text-gray-900 mb-3">Recent Transactions</h4>
            <div className="space-y-2 mb-6 max-h-48 overflow-y-auto">
              {selectedDealer.recentTransactions?.slice(0, 10).map((tx: any) => (
                <div key={tx.id} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div>
                    <div className="text-gray-900">{tx.description}</div>
                    <div className="text-sm text-gray-500">{new Date(tx.created_at).toLocaleString()}</div>
                  </div>
                  <div className={`font-semibold ${tx.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </div>
                </div>
              ))}
              {(!selectedDealer.recentTransactions || selectedDealer.recentTransactions.length === 0) && (
                <p className="text-gray-500 text-center py-4">No transactions yet</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedDealer(null);
                  setAdjustModal({ 
                    dealerId: selectedDealer.dealerId, 
                    dealerName: 'Selected Dealer' 
                  });
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Adjust Points
              </button>
              <button
                onClick={() => setSelectedDealer(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
